// src/app/services/auth.service.ts
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import {
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  User,
  UserCredential,
  GoogleAuthProvider,
  signInWithPopup,
  TwitterAuthProvider,
  signInAnonymously
} from '@angular/fire/auth';
import {
  Firestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  Timestamp
} from '@angular/fire/firestore';

import { Usuario } from '../models/usuario.interface';
import { UserService } from './user.service';
import { EstadisticasService } from './estadisticas.service';
import { PresenceService } from './presence.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private userService = inject(UserService);
  private estadisticasService = inject(EstadisticasService);
  private presenceService = inject(PresenceService);
  private _currentUser = signal<User | null>(null);
  readonly currentUser = computed(() => this._currentUser());

  constructor() {
    this.auth.onAuthStateChanged(async (user) => {
      this._currentUser.set(user);

      // Siempre reiniciar presencia
      this.presenceService.initialized = false;

      if (user) {
        await this.presenceService.setPresence();
      }
    });

  }

  async login(email: string, password: string): Promise<UserCredential> {
    try {
      const cred = await signInWithEmailAndPassword(this.auth, email, password);
      await this.createUserDocIfNotExists(cred.user);
      return cred;
    } catch (error: any) {
      console.error('[AuthService] Error en login()', error);
      throw new Error(this.getFirebaseAuthErrorMessage(error?.code));
    }
  }

  async register(email: string, password: string): Promise<UserCredential> {
    try {
      const cred = await createUserWithEmailAndPassword(this.auth, email, password);
      await this.createUserDocIfNotExists(cred.user);
      return cred;
    } catch (error: any) {
      console.error('[AuthService] Error en register()', error);
      throw new Error(this.getFirebaseAuthErrorMessage(error?.code));
    }
  }

  async loginWithGoogle(): Promise<UserCredential> {
    try {
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(this.auth, provider);
      await this.createUserDocIfNotExists(cred.user, false);
      return cred;
    } catch (error: any) {
      console.error('[AuthService] Error en loginWithGoogle()', error);
      throw new Error(this.getFirebaseAuthErrorMessage(error.code));
    }
  }

  async loginAnonymous() {
    try {
      const userCredential = await signInAnonymously(this.auth);
      await this.createUserDocIfNotExists(userCredential.user, true);
      return userCredential.user;
    } catch (error: any) {
      console.error('[AuthService] Error en loginAnonymous()', error);
      throw new Error(this.getFirebaseAuthErrorMessage(error.code));
    }
  }

  async logout(): Promise<void> {
    await this.presenceService.clearPresence();
    await signOut(this.auth);
    this.userService.limpiarUsuario();
  }

  get uid(): string | null {
    return this.auth.currentUser?.uid || null;
  }

  getCurrentUser() {
    return this.auth.currentUser;
  }

  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    return this.auth.onAuthStateChanged((user) => {
      this._currentUser.set(user);
      callback(user);
    });
  }

  initAuth(): Promise<void> {
    return new Promise(resolve => {
      const unsub = this.onAuthStateChanged(() => {
        unsub();
        resolve();
      });
    });
  }

  async getIdToken(forceRefresh = false): Promise<string> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('Usuario no autenticado');
    }
    return user.getIdToken(forceRefresh);
  }

  private async createUserDocIfNotExists(user: User, isAnonymous = false): Promise<void> {
    const ref = doc(this.firestore, 'usuarios', user.uid);
    let snapshot;

    try {
      snapshot = await getDoc(ref);
    } catch (error) {
      console.error('[AuthService] Error al obtener documento del usuario (getDoc):', ref.path, error);
      throw error;
    }

    const ip = await this.getPublicIP();
    const userAgent = navigator.userAgent;

    if (!snapshot.exists()) {
      const usuario: Usuario = {
        uid: user.uid,
        role: 'usuario básico',
        email: user.email ?? null,
        anonimo: isAnonymous,
        nombre: user.displayName ?? 'Anonimous',
        genero: 'Masculino',
        edad: 18,
        fotoPerfilUrl: 'assets/images/default-avatar.svg',
        ubicacion: null,
        activo: true,
        creadoEn: serverTimestamp() as Timestamp,
        actualizadoEn: serverTimestamp() as Timestamp,
        userAgent,
        ip,
        fotos: [],
        intereses: [],
        redesSociales: {},
        disponiblePara: [],
        preferencias: {
          buscarGenero: 'Todos',
          distanciaMaxKm: 5000,
          edadMin: 18,
          edadMax: 99,
          busca: []
        }
      };

      try {
        await setDoc(ref, usuario);
      } catch (error) {
        console.error('[AuthService] Error al crear documento de usuario (setDoc):', ref.path, error);
        throw error;
      }

      try {
        await this.estadisticasService.incrementarCampo('totalUsuarios');
        await this.estadisticasService.incrementarCampo('usuariosNuevosHoy');
      } catch (error) {
        console.error('[AuthService] Error al incrementar estadísticas:', error);
      }
    } else {
      try {
        await updateDoc(ref, {
          actualizadoEn: serverTimestamp(),
          activo: true,
          ip,
          userAgent
        });
      } catch (error) {
        console.error('[AuthService] Error al actualizar documento de usuario (updateDoc):', ref.path, error);
        throw error;
      }
    }
  }

  private async getPublicIP(): Promise<string | null> {
    try {
      const res = await fetch('https://api.ipify.org?format=json');
      const data = await res.json();
      return data.ip || null;
    } catch (error) {
      console.warn('[AuthService] No se pudo obtener IP pública:', error);
      return null;
    }
  }

  private getFirebaseAuthErrorMessage(code: string): string {
    const errores: Record<string, string> = {
      'auth/user-not-found': 'auth.user_not_found',
      'auth/wrong-password': 'auth.wrong_password',
      'auth/invalid-email': 'auth.invalid_email',
      'auth/user-disabled': 'auth.user_disabled',
      'auth/email-already-in-use': 'auth.email_already_in_use',
      'auth/weak-password': 'auth.weak_password',
      'auth/too-many-requests': 'auth.too_many_requests',
      'auth/network-request-failed': 'auth.network_error',
      'auth/internal-error': 'auth.internal_error',
      'auth/popup-closed-by-user': 'auth.popup_closed_by_user',
      'auth/cancelled-popup-request': 'auth.cancelled_popup_request',
      'auth/popup-blocked': 'auth.popup_blocked',
      'auth/redirect-cancelled-by-user': 'auth.redirect_cancelled_by_user',
      'auth/redirect-operation-pending': 'auth.redirect_operation_pending',
      'auth/account-exists-with-different-credential': 'auth.account_exists_with_different_credential',
      'auth/credential-already-in-use': 'auth.credential_already_in_use',
      'auth/operation-not-allowed': 'auth.operation_not_allowed',
      'auth/unauthorized-domain': 'auth.unauthorized_domain',
      'auth/invalid-credential': 'auth.invalid_credential',
    };

    return errores[code] || 'auth.unknown_error';
  }
}
